import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
  Button,
  CircularProgress,
  MenuItem,
  TextField,
  Typography,
} from '@material-ui/core';

import {apiGetWalletCashflows} from 'constants/walletCashflowApi';
import type {
  IWalletCashflow,
  IWalletMonthlyBudget,
  IYearMonth,
} from 'constants/walletCashflowTypes';

import './WalletCashflowPanel.scss';

interface WalletCashflowPanelProps {
  walletId: string;
  refreshToken?: number;
  activeTab?: 'budget' | 'cashflows';
  hideTabs?: boolean;
  onTabChange?: (tab: 'budget' | 'cashflows') => void;
  onLinkApplication?: () => void;
  onViewApplication?: (applicationId: string) => void;
}

type TApprovedCashflowMonth = {
  key: string;
  label: string;
};

type TApprovedCashflowTableRow = {
  id: string;
  label: string;
  total: number;
  values: Record<string, number>;
};

const MONTH_LABELS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

function lshParseDecimal(value: unknown): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function lshFormatMoney(value: unknown): string {
  return lshParseDecimal(value).toLocaleString('en-ZA', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function lshFormatPeriod(cashflow: IWalletCashflow): string {
  if (cashflow.periodStart && cashflow.periodEnd) {
    return `${cashflow.periodStart} to ${cashflow.periodEnd}`;
  }
  return 'Period not set';
}

function lshCashflowApplicationId(cashflow?: IWalletCashflow): string {
  return String(
    cashflow?.creditApplicationRef?.applicationId ||
      cashflow?.creditApplicationRefId ||
      '',
  );
}

function lshBudgetMonthKey(budget: IWalletMonthlyBudget): string {
  return `${budget.periodYear}-${String(budget.periodMonth).padStart(2, '0')}`;
}

function lshBudgetMonthLabel(budget: IWalletMonthlyBudget): string {
  const monthLabel = MONTH_LABELS[budget.periodMonth - 1] || 'Month';
  return `${monthLabel.toUpperCase()}${String(budget.periodYear).slice(-2)}`;
}

function lshGetBudgetTotals(budgets: IWalletMonthlyBudget[]) {
  return budgets.reduce(
    (totals, budget) => ({
      allocated: totals.allocated + lshParseDecimal(budget.allocatedAmount),
      consumed: totals.consumed + lshParseDecimal(budget.consumedAmount),
      remaining: totals.remaining + lshParseDecimal(budget.remainingAmount),
    }),
    {allocated: 0, consumed: 0, remaining: 0},
  );
}

function lshGetApprovedCashflowMonths(
  budgets: IWalletMonthlyBudget[],
): TApprovedCashflowMonth[] {
  const monthMap = new Map<string, TApprovedCashflowMonth>();

  budgets.forEach((budget) => {
    if (!budget.periodYear || !budget.periodMonth) return;
    const monthKey = lshBudgetMonthKey(budget);
    monthMap.set(monthKey, {
      key: monthKey,
      label: lshBudgetMonthLabel(budget),
    });
  });

  return Array.from(monthMap.values()).sort((left, right) =>
    left.key.localeCompare(right.key),
  );
}

function lshGetApprovedCashflowRows(
  budgets: IWalletMonthlyBudget[],
): TApprovedCashflowTableRow[] {
  const rows = new Map<string, TApprovedCashflowTableRow>();

  budgets.forEach((budget) => {
    const category = budget.spendCategory;
    const rowId = category?.id || category?.code || budget.id;
    const row =
      rows.get(rowId) ||
      ({
        id: rowId,
        label: category?.label || category?.code || 'Uncategorised',
        total: 0,
        values: {},
      } as TApprovedCashflowTableRow);
    const monthKey = lshBudgetMonthKey(budget);
    const amount = lshParseDecimal(budget.allocatedAmount);

    row.values[monthKey] = lshParseDecimal(row.values[monthKey]) + amount;
    row.total += amount;
    rows.set(rowId, row);
  });

  return Array.from(rows.values()).sort((left, right) =>
    left.label.localeCompare(right.label),
  );
}

function lshUsagePercent(budget: IWalletMonthlyBudget): number {
  const allocated = lshParseDecimal(budget.allocatedAmount);
  if (allocated <= 0) return 0;
  return Math.min(
    100,
    Math.round((lshParseDecimal(budget.consumedAmount) / allocated) * 100),
  );
}

export function WalletCashflowPanel({
  walletId,
  refreshToken,
  activeTab,
  hideTabs,
  onTabChange,
  onLinkApplication,
  onViewApplication,
}: WalletCashflowPanelProps) {
  const now = new Date();
  const [period, setPeriod] = useState<IYearMonth>({
    year: now.getFullYear(),
    month: now.getMonth() + 1,
  });
  const [internalActiveTab, setInternalActiveTab] = useState<
    'budget' | 'cashflows'
  >('budget');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [cashflows, setCashflows] = useState<IWalletCashflow[]>([]);
  const [selectedCashflowId, setSelectedCashflowId] = useState('');

  const loadData = useCallback(async () => {
    if (!walletId) return;
    setLoading(true);
    setError('');
    try {
      const cashflowList = await apiGetWalletCashflows(walletId);
      setCashflows(cashflowList || []);
    } catch (err: any) {
      console.error('Wallet cashflow load failed', err);
      setError(
        Array.isArray(err)
          ? err.join(', ')
          : err?.message || 'Failed to load wallet cashflow data',
      );
      setCashflows([]);
    } finally {
      setLoading(false);
    }
  }, [refreshToken, walletId]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  useEffect(() => {
    if (!cashflows.length) {
      setSelectedCashflowId('');
      return;
    }

    if (!selectedCashflowId) {
      setSelectedCashflowId(cashflows[0].id);
      return;
    }

    if (!cashflows.some((cashflow) => cashflow.id === selectedCashflowId)) {
      setSelectedCashflowId(cashflows[0].id);
    }
  }, [cashflows, selectedCashflowId]);

  const selectedCashflow = useMemo(
    () => cashflows.find((item) => item.id === selectedCashflowId),
    [cashflows, selectedCashflowId],
  );

  const periodBudgets = useMemo(() => {
    if (!selectedCashflow?.budgets?.length) return [];
    return selectedCashflow.budgets.filter(
      (budget) =>
        budget?.spendCategory &&
        budget.periodYear === period.year &&
        budget.periodMonth === period.month,
    );
  }, [period.month, period.year, selectedCashflow]);

  const periodTotals = useMemo(
    () => lshGetBudgetTotals(periodBudgets),
    [periodBudgets],
  );

  const selectedCashflowTotals = useMemo(
    () => lshGetBudgetTotals(selectedCashflow?.budgets || []),
    [selectedCashflow],
  );
  const approvedCashflowMonths = useMemo(
    () => lshGetApprovedCashflowMonths(selectedCashflow?.budgets || []),
    [selectedCashflow],
  );
  const approvedCashflowRows = useMemo(
    () => lshGetApprovedCashflowRows(selectedCashflow?.budgets || []),
    [selectedCashflow],
  );
  const approvedCashflowMonthTotals = useMemo(
    () =>
      approvedCashflowMonths.reduce<Record<string, number>>((totals, month) => {
        totals[month.key] = approvedCashflowRows.reduce(
          (sum, row) => sum + lshParseDecimal(row.values[month.key]),
          0,
        );
        return totals;
      }, {}),
    [approvedCashflowMonths, approvedCashflowRows],
  );
  const currentTab = activeTab || internalActiveTab;
  const setCurrentTab = (nextTab: 'budget' | 'cashflows') => {
    onTabChange?.(nextTab);
    if (!activeTab) setInternalActiveTab(nextTab);
  };

  if (loading && !cashflows.length) {
    return (
      <div className="wallet-cashflow-panel loading">
        <CircularProgress size={28} />
        <span>Loading wallet cashflow...</span>
      </div>
    );
  }

  return (
    <div className="wallet-cashflow-panel">
      {!hideTabs ? (
        <div className="wallet-cashflow-panel__tabs">
          <button
            type="button"
            className={currentTab === 'budget' ? 'active' : ''}
            onClick={() => setCurrentTab('budget')}
          >
            Spent vs approved
          </button>
          <button
            type="button"
            className={currentTab === 'cashflows' ? 'active' : ''}
            onClick={() => setCurrentTab('cashflows')}
          >
            Approved cashflows
          </button>
        </div>
      ) : null}

      {error ? (
        <Typography
          color="error"
          variant="body2"
          className="wallet-cashflow-panel__error"
        >
          {error}
        </Typography>
      ) : null}

      {currentTab === 'cashflows' ? (
        <div className="wallet-cashflow-panel__section">
          <div className="wallet-cashflow-panel__header-row">
            <div>
              <Typography variant="subtitle1">Approved cashflows</Typography>
              <Typography variant="body2" color="textSecondary">
                Linked application cashflows are the wallet spend-limit source.
              </Typography>
            </div>
            <div className="wallet-cashflow-panel__header-actions">
              {cashflows.length > 1 ? (
                <TextField
                  select
                  label="Approved cashflow"
                  variant="outlined"
                  size="small"
                  value={selectedCashflowId}
                  onChange={(event) =>
                    setSelectedCashflowId(event.target.value)
                  }
                >
                  {cashflows.map((cashflow) => (
                    <MenuItem key={cashflow.id} value={cashflow.id}>
                      App #
                      {cashflow.creditApplicationRef?.applicationId ||
                        cashflow.id}
                    </MenuItem>
                  ))}
                </TextField>
              ) : null}
              {onViewApplication &&
              selectedCashflow &&
              lshCashflowApplicationId(selectedCashflow) ? (
                <Button
                  color="primary"
                  variant="outlined"
                  size="small"
                  onClick={() =>
                    onViewApplication(
                      lshCashflowApplicationId(selectedCashflow),
                    )
                  }
                >
                  View application
                </Button>
              ) : null}
            </div>
          </div>

          {!cashflows.length ? (
            <div className="wallet-cashflow-panel__empty">
              <Typography variant="body2" color="textSecondary">
                No approved cashflow is linked to this wallet yet.
              </Typography>
              {onLinkApplication ? (
                <Button
                  color="primary"
                  variant="contained"
                  size="small"
                  onClick={onLinkApplication}
                >
                  Link approved application
                </Button>
              ) : null}
            </div>
          ) : selectedCashflow ? (
            <div className="wallet-cashflow-approved">
              <div className="wallet-cashflow-approved__summary">
                <div>
                  <span>Approved cashflow</span>
                  <strong>
                    Application #
                    {selectedCashflow.creditApplicationRef?.applicationId ||
                      selectedCashflow.creditApplicationRefId}
                  </strong>
                  <em>{lshFormatPeriod(selectedCashflow)}</em>
                </div>
                <div>
                  <span>Total approved</span>
                  <strong>
                    R{lshFormatMoney(selectedCashflowTotals.allocated)}
                  </strong>
                </div>
                <div>
                  <span>Total spent</span>
                  <strong>
                    R{lshFormatMoney(selectedCashflowTotals.consumed)}
                  </strong>
                </div>
                <div>
                  <span>Total left</span>
                  <strong>
                    R{lshFormatMoney(selectedCashflowTotals.remaining)}
                  </strong>
                </div>
              </div>

              {approvedCashflowRows.length ? (
                <div className="wallet-cashflow-approved__table-scroll">
                  <table className="wallet-cashflow-approved__table">
                    <thead>
                      <tr>
                        <th scope="col">Cost item</th>
                        {approvedCashflowMonths.map((month) => (
                          <th key={month.key} scope="col">
                            {month.label}
                          </th>
                        ))}
                        <th scope="col">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {approvedCashflowRows.map((row) => (
                        <tr key={row.id}>
                          <th scope="row">{row.label}</th>
                          {approvedCashflowMonths.map((month) => {
                            const amount = lshParseDecimal(
                              row.values[month.key],
                            );
                            return (
                              <td key={`${row.id}-${month.key}`}>
                                {amount ? `R${lshFormatMoney(amount)}` : '-'}
                              </td>
                            );
                          })}
                          <td>R{lshFormatMoney(row.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <th scope="row">Total</th>
                        {approvedCashflowMonths.map((month) => (
                          <td key={`total-${month.key}`}>
                            {approvedCashflowMonthTotals[month.key]
                              ? `R${lshFormatMoney(
                                  approvedCashflowMonthTotals[month.key],
                                )}`
                              : '-'}
                          </td>
                        ))}
                        <td>
                          R{lshFormatMoney(selectedCashflowTotals.allocated)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              ) : (
                <Typography variant="body2" color="textSecondary">
                  This approved cashflow has no monthly budget lines yet.
                </Typography>
              )}
            </div>
          ) : null}
        </div>
      ) : null}

      {currentTab === 'budget' ? (
        <div className="wallet-cashflow-panel__section">
          <div className="wallet-cashflow-panel__header-row">
            <div className="wallet-cashflow-panel__period">
              <TextField
                select
                label="Month"
                variant="outlined"
                size="small"
                value={period.month}
                onChange={(event) =>
                  setPeriod((current) => ({
                    ...current,
                    month: Number(event.target.value),
                  }))
                }
              >
                {MONTH_LABELS.map((label, index) => (
                  <MenuItem key={label} value={index + 1}>
                    {label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Year"
                variant="outlined"
                size="small"
                type="number"
                value={period.year}
                onChange={(event) =>
                  setPeriod((current) => ({
                    ...current,
                    year: Number(event.target.value),
                  }))
                }
              />
              {cashflows.length ? (
                <TextField
                  select
                  label="Approved cashflow"
                  variant="outlined"
                  size="small"
                  value={selectedCashflowId}
                  onChange={(event) =>
                    setSelectedCashflowId(event.target.value)
                  }
                >
                  {cashflows.map((cashflow) => (
                    <MenuItem key={cashflow.id} value={cashflow.id}>
                      App #
                      {cashflow.creditApplicationRef?.applicationId ||
                        cashflow.id}
                    </MenuItem>
                  ))}
                </TextField>
              ) : null}
            </div>
          </div>

          {!selectedCashflow ? (
            <div className="wallet-cashflow-panel__empty">
              <Typography variant="body2" color="textSecondary">
                No approved cashflow is linked to this wallet yet.
              </Typography>
              {onLinkApplication ? (
                <Button
                  color="primary"
                  variant="contained"
                  size="small"
                  onClick={onLinkApplication}
                >
                  Link approved application
                </Button>
              ) : null}
            </div>
          ) : (
            <>
              <div className="wallet-cashflow-panel__cashflow-summary">
                <div>
                  <span>Approved cashflow</span>
                  <strong>
                    Application #
                    {selectedCashflow.creditApplicationRef?.applicationId ||
                      selectedCashflow.creditApplicationRefId}
                  </strong>
                  <em>{lshFormatPeriod(selectedCashflow)}</em>
                </div>
                <div>
                  <span>Total approved</span>
                  <strong>
                    R{lshFormatMoney(selectedCashflowTotals.allocated)}
                  </strong>
                </div>
                <div>
                  <span>Total spent</span>
                  <strong>
                    R{lshFormatMoney(selectedCashflowTotals.consumed)}
                  </strong>
                </div>
                <div>
                  <span>Total left</span>
                  <strong>
                    R{lshFormatMoney(selectedCashflowTotals.remaining)}
                  </strong>
                </div>
              </div>

              <div className="wallet-cashflow-panel__totals">
                <div>
                  <span>Approved this month</span>
                  <strong>R{lshFormatMoney(periodTotals.allocated)}</strong>
                </div>
                <div>
                  <span>Spent this month</span>
                  <strong>R{lshFormatMoney(periodTotals.consumed)}</strong>
                </div>
                <div>
                  <span>Budget left</span>
                  <strong>R{lshFormatMoney(periodTotals.remaining)}</strong>
                </div>
              </div>

              <table className="wallet-cashflow-budget-table">
                <thead>
                  <tr>
                    <th>Category</th>
                    <th>Approved budget</th>
                    <th>Spent</th>
                    <th>Budget left</th>
                    <th>Usage</th>
                  </tr>
                </thead>
                <tbody>
                  {periodBudgets.map((budget) => {
                    const category = budget.spendCategory;
                    const usage = lshUsagePercent(budget);
                    return (
                      <tr key={budget.id}>
                        <td>{category?.label || category?.code || '-'}</td>
                        <td>R{lshFormatMoney(budget.allocatedAmount)}</td>
                        <td>R{lshFormatMoney(budget.consumedAmount)}</td>
                        <td>R{lshFormatMoney(budget.remainingAmount)}</td>
                        <td>
                          <div className="wallet-cashflow-usage">
                            <span style={{width: `${usage}%`}} />
                          </div>
                          <small>{usage}%</small>
                        </td>
                      </tr>
                    );
                  })}
                  {!periodBudgets.length ? (
                    <tr>
                      <td colSpan={5}>
                        No approved application cashflow budget found for this
                        period.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}

export default WalletCashflowPanel;
